import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {unminimizeCommentMutation as unminimizeCommentMutationQueryId} from '@github-ui/server-persisted-queries'
import type {UnminimizeCommentMutationMutation} from '@github-ui/server-persisted-queries/types/unminimizeCommentMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

function minimizationPath(owner?: string, repo?: string): string {
  if (owner && repo) {
    return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/comments/minimization?queryId=${unminimizeCommentMutationQueryId}`
  }

  const [, inferredOwner, inferredRepo] = window.location.pathname.split('/')
  if (!inferredOwner || !inferredRepo) {
    throw new Error('Failed to infer repository path for unminimizing comment')
  }

  return `/${encodeURIComponent(inferredOwner)}/${encodeURIComponent(inferredRepo)}/comments/minimization?queryId=${unminimizeCommentMutationQueryId}`
}

type UnminimizeOperation = SetFieldsOperation & {
  unminimizeInput: {
    owner?: string
    repo?: string
    onCompleted?: () => void
    onError?: (error?: Error) => void
  }
}

type UnminimizeResult = {
  isMinimized: boolean
  minimizedReason: string | null
}

async function submitUnminimizeComment(operation: SetFieldsOperation): Promise<UnminimizeResult> {
  const {id, unminimizeInput} = operation as UnminimizeOperation
  const response = await reactFetchJSON(minimizationPath(unminimizeInput.owner, unminimizeInput.repo), {
    method: 'DELETE',
    body: {input: {subjectId: id}},
  })
  if (!response.ok) throw new Error(`Failed to unminimize comment (${response.status})`)

  const payload = (await response.json()) as {
    errors?: Array<{message?: string}>
    data?: UnminimizeCommentMutationMutation
  }
  const comment = payload.data?.unminimizeComment?.unminimizedComment
  if (!comment) throw new Error(payload.errors?.[0]?.message ?? 'Failed to unminimize comment')
  return {isMinimized: comment.isMinimized, minimizedReason: comment.minimizedReason ?? null}
}

export function useUnminimizeCommentMutation() {
  const mutation = useEntityMutation<UnminimizeResult>({
    mutationFn: submitUnminimizeComment,
    getConfirmedOperation: (result, operation) => ({
      type: 'setFields',
      id: operation.id,
      fields: {
        isHidden: result.isMinimized,
        minimizedReason: result.minimizedReason,
        pendingMinimizeReason: result.minimizedReason,
      },
    }),
    onSuccess: (_result, {operation}) => {
      ;(operation as UnminimizeOperation).unminimizeInput.onCompleted?.()
    },
    onError: (error, {operation}) => {
      ;(operation as UnminimizeOperation).unminimizeInput.onError?.(error)
    },
  })

  return useCallback(
    ({
      input: {id, owner, repo},
      onCompleted,
      onError,
    }: {
      input: {id: string; owner?: string; repo?: string}
      onCompleted?: () => void
      onError?: (error?: Error) => void
    }) => {
      const operation: UnminimizeOperation = {
        type: 'setFields',
        id,
        fields: {isHidden: false, minimizedReason: null, pendingMinimizeReason: null},
        unminimizeInput: {owner, repo, onCompleted, onError},
      }
      try {
        mutation.mutate({operation})
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Failed to unminimize comment'))
      }
    },
    [mutation],
  )
}
